/**
 * «Вопросы клиентов» → «По группам» (`/knowledge?tab=questions&view=groups`): категория → группа → формулировки.
 * Группа = карточка и вопросы, на которые она отвечает; рядом — «Похоже, отвечает эта карточка» (предположение по
 * смыслу, редактор привязывает или отклоняет); без карточки — будущая карточка (близкие по смыслу) или разовый.
 * Ответ — один на группу: микрофон группы открывает её главный вопрос. Формулировки раскрытой группы — третий
 * уровень: сдвинуты под заголовок группы, на вертикальной линии, мельче. «карточка» группы и формулировки
 * открывают карточки в панели справа (`CardPanel`, состояние — в адресе у `KnowledgePage`). Спека
 * `docs/specs/2026-10-26-kb-question-groups-ux-design.md`, прототип `/assets/design/kb-question-groups-concept.html`.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BookOpen, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Mic } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import type { PanelCard } from "@/components/knowledge/CardPanel";
import { Highlight, QuestionRow } from "@/components/knowledge/QuestionRow";
import { type GroupList, type GroupMember, linkQuestions, type QuestionGroup, rejectInGroup } from "@/lib/knowledge";
import { errText } from "@/pages/KnowledgeCardPage";
import { MINE, mineTone } from "@/pages/KnowledgeQuestionPage";

const small =
  "min-h-7 rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-[12px] text-fg-muted hover:text-fg disabled:opacity-50";

type Props = {
  data: GroupList;
  words: string[];
  canEdit: boolean;
  categoryLabel: (code: string) => string;
  kindLabel: (kind: string) => string | undefined;
  href: (id: number) => string;
  remember: (id: number) => void;
  openVoice: (id: number) => void;
  /** Форма новой карточки с заголовком и вопросами группы (`NewCard` вкладки). */
  newCard: (title: string, questionIds: number[], onDone: () => void) => ReactNode;
  /** Карточки в панель справа; `group` — ключ группы для подсветки. */
  openCards: (cards: PanelCard[], title: string, group?: string) => void;
  /** Группа, по которой открыта панель. */
  selected: string | null;
  /** Период ссылок «чаты · N» / «звонки · N» у формулировки; нет — нет права «Чаты». */
  dialogsDays?: number | undefined;
};

/** Карточки группы для панели: карточка группы первой, затем карточки её формулировок по числу формулировок. */
export function groupCards(g: QuestionGroup): PanelCard[] {
  const n = new Map<number, { c: PanelCard; k: number }>();
  for (const m of g.members) for (const c of m.cards) n.set(c.id, { c, k: (n.get(c.id)?.k ?? 0) + 1 });
  const rest = [...n.values()].filter((x) => x.c.id !== g.card?.id).sort((a, b) => b.k - a.k).map((x) => x.c);
  return g.card ? [g.card, ...rest] : rest;
}

export default function QuestionGroups(p: Props) {
  const [closedCats, setClosedCats] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [singles, setSingles] = useState<Set<string>>(new Set());
  const поиск = p.words.length > 0;
  // поиск: группы с совпадением раскрыты сразу (первые 30 — чтобы не раскрыть полсотни карточек)
  useEffect(() => {
    if (!поиск) return;
    const keys = p.data.categories.flatMap((c) => c.items.filter((g) => g.match && g.type !== "single").map((g) => g.key));
    setOpen(new Set(keys.slice(0, 30)));
  }, [поиск, p.data]);
  const flip = (s: Set<string>, set: (x: Set<string>) => void, k: string) => {
    const n = new Set(s);
    if (n.has(k)) n.delete(k);
    else n.add(k);
    set(n);
  };
  const всеКатегории = p.data.categories.map((c) => c.code);
  const всёСвёрнуто = open.size === 0 && singles.size === 0 && всеКатегории.every((k) => closedCats.has(k));
  const категорииОткрыты = open.size === 0 && closedCats.size === 0;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={`${small} flex items-center gap-1`}
          disabled={всёСвёрнуто}
          onClick={() => {
            setOpen(new Set());
            setSingles(new Set());
            setClosedCats(new Set(всеКатегории));
          }}
          title="Свернуть категории и группы — останется список категорий"
        >
          <ChevronsDownUp size={13} aria-hidden /> Свернуть все группы
        </button>
        <button
          type="button"
          className={`${small} flex items-center gap-1`}
          disabled={категорииОткрыты}
          onClick={() => {
            setOpen(new Set());
            setClosedCats(new Set());
          }}
          title="Раскрыть категории, группы оставить свёрнутыми — видно, как вопросы побиты на группы"
        >
          <ChevronsUpDown size={13} aria-hidden /> Раскрыть категории
        </button>
        {open.size > 0 && <span className="text-[11.5px] text-fg-faint">раскрыто групп: {open.size}</span>}
      </div>
      {p.data.categories.map((c) => {
        const свёрнута = closedCats.has(c.code);
        const группы = c.items.filter((g) => g.type !== "single");
        const разовые = c.items.filter((g) => g.type === "single");
        const разовыеОткрыты = поиск || singles.has(c.code);
        return (
          <section key={c.code} className="overflow-hidden rounded-[var(--radius-card)] border border-border">
            <button
              type="button"
              className="flex w-full flex-wrap items-baseline gap-x-2 bg-bg-subtle px-3 py-2 text-left"
              aria-expanded={!свёрнута}
              onClick={() => flip(closedCats, setClosedCats, c.code)}
            >
              {свёрнута ? <ChevronRight size={14} className="self-center" /> : <ChevronDown size={14} className="self-center" />}
              <span className="text-[13px] font-semibold text-fg">{p.categoryLabel(c.code)}</span>
              <span className="text-[12px] text-fg-muted">
                {c.mentions} упоминаний · {c.groups} групп · с карточкой {c.with_card}
                {c.singles > 0 && ` · разовых ${c.singles}`}
              </span>
            </button>
            {!свёрнута && (
              <ul className="divide-y divide-border">
                {группы.map((g) => (
                  <Group key={g.key} g={g} p={p} opened={open.has(g.key)} toggle={() => flip(open, setOpen, g.key)} />
                ))}
                {разовые.length > 0 && !разовыеОткрыты && (
                  <li>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left text-[12.5px] text-fg-muted hover:bg-bg-subtle hover:text-fg"
                      onClick={() => flip(singles, setSingles, c.code)}
                    >
                      ещё {разовые.length} разовых вопросов →
                    </button>
                  </li>
                )}
                {разовыеОткрыты &&
                  разовые.flatMap((g) => g.members.slice(0, 1).map((m) => ({ g, m }))).map(({ g, m }) => (
                    <QuestionRow
                      key={g.key}
                      x={m}
                      href={p.href(g.representative_id)}
                      category={p.categoryLabel(g.category)}
                      kindLabel={p.kindLabel(g.kind)}
                      canEdit={p.canEdit}
                      words={p.words}
                      onRemember={() => p.remember(g.representative_id)}
                      onVoice={() => p.openVoice(g.representative_id)}
                      onCards={(cards) => p.openCards(cards, m.text)}
                      dialogsDays={p.dialogsDays}
                    />
                  ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

function Group({ g, p, opened, toggle }: { g: QuestionGroup; p: Props; opened: boolean; toggle: () => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const обновить = () => qc.invalidateQueries({ queryKey: ["kb"] });
  const привязать = useMutation({
    mutationFn: (ids: number[]) => linkQuestions(g.card!.id, ids),
    onSuccess: (r) => {
      setNote(`Привязано формулировок: ${r.linked}.`);
      обновить();
    },
    onError: (e) => setNote(`Не удалось привязать: ${errText(e)}. Связи не изменились.`),
  });
  const отклонить = useMutation({
    mutationFn: (x: { id: number; card: number | null }) => rejectInGroup(x.id, x.card),
    onSuccess: (_r, x) => {
      setNote(x.card ? "Предложение отклонено: вопрос больше не предлагается к этой карточке." : "Вопрос вынесен из группы — теперь он разовый.");
      обновить();
    },
    onError: (e) => setNote(`Не получилось: ${errText(e)}`),
  });
  const привязанные = g.members.filter((m) => m.relation === "linked");
  const похожие = g.members.filter((m) => m.relation === "suggested");
  const прочие = g.members.filter((m) => m.relation === "member");
  const row = (m: GroupMember, actions?: ReactNode) => (
    <QuestionRow
      key={m.id}
      x={m}
      href={p.href(m.id)}
      category={p.categoryLabel(m.category)}
      kindLabel={p.kindLabel(m.kind)}
      canEdit={p.canEdit}
      words={p.words}
      onRemember={() => p.remember(m.id)}
      onVoice={() => p.openVoice(m.id)}
      onCards={(cards) => p.openCards(cards, m.text)}
      actions={actions}
      dialogsDays={p.dialogsDays}
      compact
    />
  );
  const id = `kbg-${g.key.replace(":", "-")}`;
  const карточки = groupCards(g);
  const выбрана = p.selected === g.key;
  return (
    <li className={`border-l-2 ${выбрана ? "border-accent bg-accent/5" : opened ? "border-accent/60 bg-bg-subtle/60" : "border-transparent"}`}>
      <div className="group flex items-start gap-3 px-3 py-2 hover:bg-bg-subtle focus-within:bg-bg-subtle">
        <span className="w-9 shrink-0 pt-0.5 text-right text-[15px] tabular-nums text-fg" title="Сумма упоминаний формулировок группы за период">
          {g.mentions}
        </span>
        <div className="min-w-0 flex-1">
          <button type="button" className="flex items-start gap-1 text-left text-[13px] font-medium text-fg hover:underline" aria-expanded={opened} aria-controls={id} onClick={toggle}>
            {opened ? <ChevronDown size={14} className="mt-0.5 shrink-0 text-accent" /> : <ChevronRight size={14} className="mt-0.5 shrink-0" />}
            <span>
              <Highlight text={g.title} words={p.words} />
            </span>
          </button>
          {g.card && (
            <div className="ml-[18px] text-[12px] text-fg-muted">
              <Highlight text={g.representative_text} words={p.words} />
            </div>
          )}
          <div className="ml-[18px] mt-0.5 flex flex-wrap gap-x-3 text-[11.5px] text-fg-muted">
            <span>{g.question_count} формулировок</span>
            {карточки.length > 0 && (
              <button
                type="button"
                className={g.card ? "text-ok underline" : "underline hover:text-fg"}
                onClick={() => p.openCards(карточки, g.title, g.key)}
                title={`Открыть справа: ${карточки.map((k) => k.title).join(" · ")}`}
              >
                {g.card ? (карточки.length > 1 ? `карточка + ещё ${карточки.length - 1}` : "карточка") : `карточки формулировок · ${карточки.length}`}
              </button>
            )}
            {g.card ? null : g.type === "live" || g.type === "action" ? (
              <span className="rounded border border-warn bg-warn/10 px-1 text-fg">{g.type === "live" ? "Данные клиента" : "Действие в CRM"}</span>
            ) : (
              <span className="text-fg-faint">нужна карточка</span>
            )}
            {g.unanswered > 0 && <span className="text-warn">без полного ответа {g.unanswered}</span>}
            {g.suggested_count > 0 && <span>похожих {g.suggested_count}</span>}
          </div>
        </div>
        <span className={`hidden w-36 shrink-0 pt-0.5 text-right text-[12px] sm:block ${mineTone(g.mine_status)}`}>
          {g.mine_status === "done" || g.mine_status === "used" ? "✓ " : ""}
          {MINE[g.mine_status]}
        </span>
        {карточки.length > 0 ? (
          <button
            type="button"
            className={`grid size-9 shrink-0 place-items-center rounded-[var(--radius-ui)] border text-fg-muted hover:border-accent hover:text-accent ${
              выбрана ? "border-accent bg-accent/10 text-accent" : "border-border"
            }`}
            onClick={() => p.openCards(карточки, g.title, g.key)}
            aria-label={`Карточки группы справа: ${g.title}`}
            title={`Карточки группы справа · ${карточки.length}`}
          >
            <BookOpen size={15} />
          </button>
        ) : (
          // место кнопки — чтобы статус и микрофон групп стояли одной колонкой
          <span className="hidden size-9 shrink-0 sm:block" aria-hidden />
        )}
        <button
          type="button"
          className="grid size-9 shrink-0 place-items-center rounded-[var(--radius-ui)] border border-border text-fg-muted group-hover:border-accent group-hover:text-accent group-focus-within:border-accent group-focus-within:text-accent"
          onClick={() => p.openVoice(g.representative_id)}
          aria-label={`Ответить голосом на группу: ${g.title}`}
          title="Один ответ на всю группу — запись начнётся сразу"
        >
          <Mic size={15} />
        </button>
      </div>
      {opened && (
        // третий уровень: под заголовком группы, на линии от её стрелки (60 px = поля + колонка числа + зазор)
        <div id={id} className="mb-2 ml-[66px] border-l-2 border-border pb-1">
          {note && (
            <div className="px-3 pt-2 text-[12px] text-fg" aria-live="polite">
              {note}
            </div>
          )}
          {g.type === "live" && <div className="px-3 pt-2 text-[12px] text-fg-muted">Ответ требует данных клиента. Карточка не нужна.</div>}
          {g.type === "action" && <div className="px-3 pt-2 text-[12px] text-fg-muted">Ответ требует действия в CRM.</div>}
          {p.canEdit && g.type === "future" && (
            <div className="px-3 pt-2">
              {creating ? (
                p.newCard(g.representative_text, g.members.map((m) => m.id), () => setCreating(false))
              ) : (
                <button type="button" className={small} onClick={() => setCreating(true)} title="Карточка с заголовком главного вопроса; все формулировки группы привяжутся к ней">
                  Сделать карточку
                </button>
              )}
            </div>
          )}
          {привязанные.length > 0 && <div className="px-3 pt-2 text-[11.5px] text-fg-faint">Привязанные формулировки · {привязанные.length}</div>}
          <ul className="divide-y divide-border/60">
            {привязанные.map((m) => row(m))}
            {прочие.map((m) =>
              row(
                m,
                p.canEdit && g.type !== "single" ? (
                  <button type="button" className={`${small} mt-1`} disabled={отклонить.isPending} onClick={() => отклонить.mutate({ id: m.id, card: null })} title="Вопрос не про то же — сделать разовым">
                    Вынести вопрос
                  </button>
                ) : undefined,
              ),
            )}
          </ul>
          {похожие.length > 0 && (
            <div className="mb-1 ml-2 mt-2 rounded-r-[var(--radius-ui)] border-l-2 border-accent bg-bg-subtle">
              <div className="flex flex-wrap items-center gap-2 px-3 pt-2">
                <span className="text-[12.5px] font-semibold text-fg">Похоже, отвечает эта карточка · {похожие.length}</span>
                {p.canEdit && (
                  <button type="button" className={`${small} ml-auto`} disabled={привязать.isPending} onClick={() => привязать.mutate(похожие.map((m) => m.id))}>
                    Привязать все
                  </button>
                )}
              </div>
              <div className="px-3 pb-1 text-[11.5px] text-fg-muted">
                Это предположение. Пока редактор не подтвердил связь, карточка не считается ответом на эти вопросы.
              </div>
              <ul className="divide-y divide-border/60">
                {похожие.map((m) =>
                  row(
                    m,
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11.5px] text-fg-muted">
                      <span>Близко по смыслу</span>
                      {p.canEdit && (
                        <>
                          <button type="button" className={small} disabled={привязать.isPending} onClick={() => привязать.mutate([m.id])}>
                            Привязать
                          </button>
                          <button type="button" className={small} disabled={отклонить.isPending} onClick={() => отклонить.mutate({ id: m.id, card: g.card!.id })}>
                            Не то
                          </button>
                        </>
                      )}
                    </div>,
                  ),
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
